import { makeObservable, observable, action, runInAction } from 'mobx';
import { ApplicationSdk } from '../../../core/sdk';
import BaseAdminStore, { AdminApiResponse } from '../../../shared/stores/baseAdminStore';
import { MatenRequestResponse } from '../../../shared';

export class KihubDatabaseStore {
    public id: number;
    public label: string;
    public description: string;
    public enabled: boolean;

    constructor(id: number, label: string, description: string, enabled: boolean) {
        makeObservable(this, {
            id: observable,
            label: observable,
            description: observable,
            enabled: observable,
        });

        this.id = id;
        this.label = label;
        this.description = description;
        this.enabled = enabled;
    }

    public async edit(sdk: ApplicationSdk, store: KihubAdminStore): Promise<void> {
        const databaseEditResponse = await sdk.getDialogMultiInput({
            title: 'Wissensdatenbank bearbeiten',
            fields: [
                {
                    label: 'Wissensdatenbank Name',
                    key: 'label',
                    type: 'text',
                },
                {
                    label: 'Wissensdatenbank Beschreibung',
                    key: 'description',
                    type: 'text',
                },
            ],
            initialValue: {
                label: this.label,
                description: this.description,
            },
            message: 'Hier können Sie die Einstellungen der Wissensdatenbank bearbeiten.',
            onConfirm: async (values: any) =>
                store.saveStore(this.id, values.label, values.description),
        });

        if (databaseEditResponse.confirmed) {
            runInAction(() => {
                const { label, description } =
                    (
                        databaseEditResponse.response as {
                            data?: { label?: string; description?: string };
                        }
                    )?.data || {};
                this.label = label || '';
                this.description = description || '';
            });

            sdk.showSuccessToast('Wissensdatenbank erfolgreich aktualisiert.');
        } else if (databaseEditResponse.error) {
            switch (databaseEditResponse.error) {
                default:
                    sdk.showErrorToast(
                        `Unbekannter Fehler beim Aktualisieren der Wissensdatenbank`
                    );
                    break;
            }
        }
    }

    public async activate(store: KihubAdminStore): Promise<MatenRequestResponse> {
        const enableResponse: MatenRequestResponse = await store.enableStore(this.id);
        runInAction(() => {
            if (enableResponse.success) {
                this.enabled = true; // Update enabled status after successful activation
            }
        });
        return enableResponse;
    }
}

/**
 * The KIHub Admin Store for managing admin settings
 */
class KihubAdminStore extends BaseAdminStore {
    // KIHub-specific properties
    public openai_api_key: string = '';
    public openai_has_key: boolean = false;

    public kihubDatabases: KihubDatabaseStore[] = [];

    public settings: any = {};

    constructor(sdk: ApplicationSdk) {
        super(sdk);

        makeObservable(this, {
            openai_api_key: observable,
            openai_has_key: observable,
            kihubDatabases: observable,
            settings: observable,
            updateOpenAIKey: action,
            // saveSettings: action,
        });
    }

    // Implement required abstract methods
    protected getApiEndpoint(): string {
        return 'kihub';
    }

    protected getApiPath(): string {
        return 'admin/get';
    }

    protected buildApiParams(): any {
        return {};
    }

    protected processApiResponse(response: AdminApiResponse): any {
        runInAction(() => {
            this.openai_has_key = response.json.validOpenAiToken || false;
            this.kihubDatabases = (response.json.kihubStores || []).map(
                (store: any) =>
                    new KihubDatabaseStore(store.id, store.label, store.description, store.enabled)
            );
        });

        return response.json;
    }

    // KIHub-specific methods
    public updateOpenAIKey(key: string): void {
        this.openai_api_key = key;
    }

    public async saveApiKey(apiKey: string): Promise<MatenRequestResponse> {
        try {
            const response = await this.makeApiCall('kihub', 'admin/api-key/update', {
                openai_api_key: apiKey,
            });

            if (response.status === 200) {
                runInAction(() => {
                    this.openai_has_key = true;
                    this.openai_api_key = apiKey;
                });
                this.fetchData();
                return { success: true, status: 200 };
            } else if (response.status === 400) {
                return {
                    success: false,
                    status: 400,
                    error: response.json.error || 'UNKNOWN_ERROR',
                };
            }
        } catch (error) {
            return { success: false, status: 400, error: 'Unbekannter Fehler' };
        }

        return { success: false };
    }

    public async enableStore(storeId: number): Promise<MatenRequestResponse> {
        try {
            const response = await this.makeApiCall('kihub', 'admin/module/update', {
                store_id: storeId,
                enable: true,
            });

            if (response.status === 200) {
                return { success: true, status: 200, data: response.json };
            } else {
                return { success: false, status: response.status, error: response.json.error };
            }
        } catch (error) {
            return { success: false, status: 500 };
        }
    }

    public async saveStore(
        storeId: number,
        label: string,
        description: string
    ): Promise<MatenRequestResponse> {
        try {
            const response = await this.makeApiCall('kihub', 'admin/module/update', {
                store_id: storeId,
                label: label,
                description: description,
            });

            if (response.status === 200) {
                return { success: true, status: 200, data: response.json };
            } else {
                return { success: false, status: response.status, error: response.json.error };
            }
        } catch (error) {
            return { success: false, status: 500 };
        }
    }

    public async init(): Promise<void> {
        await super.init();
    }
}

export default KihubAdminStore;
